<template>
  <banners :is-ultra="true">
    <div
      class="container-max m-auto"
      :class="{
        'min-h-[94vh] bg-steel-100': !isDesktop,
      }"
    >
      <div class="flex items-center justify-between bg-white px-3 py-8">
        <h1 class="text-4xl">Capacity</h1>

        <div
          v-if="showCapacityButtons"
          class="flex gap-3"
          :class="{
            'fixed bottom-0 z-20 -ml-3 flex w-full items-center justify-center border-t border-steel-300 bg-white px-4 pb-8 pt-5 shadow-md [&>*]:flex-1':
              isMobile,
          }"
        >
          <ButtonComponent
            v-if="selectedTab === 'Potential' && potential_trucks?.length > 0"
            variant="primary"
            class="!min-w-[160px]"
            :is-loading="isBulkPostingPotentialTruckPostings"
            :disabled="
              selectedPotentialCapacities?.length === 0 ||
              isBulkPostingPotentialTruckPostings
            "
            @click="handlePostSuggestedCapacities()"
          >
            Post Suggested
            <span
              v-if="selectedPotentialCapacities?.length > 0"
              class="pl-1"
            >
              ({{ selectedPotentialCapacities?.length }})
            </span>
          </ButtonComponent>

          <ButtonComponent
            v-else-if="selectedTab === 'Active' || selectedTab === 'Expired'"
            variant="primary"
            :disabled="filtered_carrier_vehicles_length === 0"
            @click="toggleCapacityModal"
          >
            Post Capacity
          </ButtonComponent>
        </div>
      </div>

      <div class="flex gap-3">
        <CapacityTable
          :is-desktop="isDesktop"
          :selected-tab="selectedTab"
          :selected-potential-capacities="selectedPotentialCapacities"
          :available-potential-postings="allAvailablePotentialPostings"
          :is-all-available-potential-postings-selected="
            isAllAvailablePotentialPostingsSelected
          "
          @handle-tab-selection="handleTabSelection"
          @handle-select-potential-capacity="handleSelectPotentialCapacity"
          @handle-select-all-potential-capacities="
            handleSelectAllPotentialCapacities($event)
          "
          @handle-post-suggested-capacities="
            handlePostSuggestedCapacities($event)
          "
        ></CapacityTable>
      </div>

      <post-capacity-modal
        v-if="isCapacityModalVisible"
        :visible="isCapacityModalVisible"
        @close="toggleCapacityModal"
        @error="handlePostTruckError"
        @handle-tab-selection="handleTabSelection"
        @failed-capacity-notification="failedCapacityNotifcation"
      ></post-capacity-modal>

      <NotificationComponent
        v-show="showTruckNotication"
        title="Unable to Post Truck"
        @close="handleCloseTruckNotification"
      >
        <div
          v-if="errorMessage?.length > 0"
          class="text-left"
        >
          <p class="mb-2 text-sm font-normal text-steel-800">
            The following issues occured:
          </p>
          <p
            v-for="(error, errorIndex) in errorMessage"
            :key="errorIndex"
            class="text-sm font-normal text-steel-700"
          >
            {{ error }}.
          </p>

          <span
            class="ml-auto mr-2 flex w-max cursor-pointer text-sm text-sun-700 underline"
            @click="handleRetry"
          >
            Retry
          </span>
        </div>
      </NotificationComponent>
    </div>
  </banners>
</template>

<script setup>
import { onBeforeMount, ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import {
  Banners,
  ButtonComponent,
  NotificationComponent,
  CapacityTable,
  PostCapacityModal,
  SuggestedCapacityNotification,
  CapacityNotification,
} from '@/components';
import { useScreenSize } from '@/composables';
import { capitalizeWords } from '@/utils';

const router = useRouter();

const store = useStore();
const { screenSize } = useScreenSize();

const isDesktop = computed(() => screenSize.value === 'desktop');
const isMobile = computed(() => screenSize.value === 'mobile');

const selectedTab = ref('Active');

const handleTabSelection = (tab) => {
  selectedTab.value = tab;

  const formattedTab =
    tab === 'Potential' ? '#suggested' : `#${tab?.toLowerCase()}`;

  router.push({
    hash: formattedTab,
  });
};

onBeforeMount(async () => {
  const carrier_id =
    store.state.users?.currentUser?.requested_carrier_id ||
    store.state.users?.currentUser?.company?.carrier_id;

  const existingHash = router.currentRoute.value?.hash;
  if (existingHash) {
    const hashTab =
      existingHash === '#suggested'
        ? 'Potential'
        : capitalizeWords(existingHash.replace('#', ''));

    handleTabSelection(hashTab);
  }

  await Promise.all([
    store.commit('loads/setIsPotentialTruckPostingLoading', true),
    store.dispatch('loads/getTruckPostings', { query: '?q[is_active_eq]=1' }),
    store.dispatch('loads/getExpiredTruckPostings', {}),
    store.dispatch('loads/getEquipmentTypes'),
    store.dispatch('loads/getCarrierVehicles', {
      id: carrier_id,
      page: 1,
      query:
        '&q[without_active_truck_postings]=1&q[is_eld_active_eq]=1&q[s]=truck_number+asc',
    }),
    store.dispatch(
      'brokers/getCompanyById',
      store.state.users?.currentUser?.company?.id
    ),
  ]);
});

const selectedPotentialCapacities = ref([]);

const handleSelectPotentialCapacity = (id) => {
  if (selectedPotentialCapacities?.value?.find((el) => el === id)) {
    const filteredCapacities = selectedPotentialCapacities.value.filter(
      (el) => el !== id
    );

    selectedPotentialCapacities.value = filteredCapacities;
  } else {
    selectedPotentialCapacities.value = [
      ...selectedPotentialCapacities.value,
      id,
    ];
  }
};

const truck_postings = computed(() => store.state.loads?.truck_postings);
const potential_trucks = computed(
  () => store.state.loads?.potential_truck_postings
);

const requireTruckNumberForPosting = computed(
  () =>
    store.state.users.featureFlags?.fe_require_truck_number_for_posting?.enabled
);

const allAvailablePotentialPostings = computed(
  () =>
    potential_trucks.value
      ?.filter(
        (el) =>
          el.id &&
          el.posted_equipment_type !== 'other' &&
          el.origin_area_id &&
          el.start_date &&
          el?.posted_equipment_type &&
          (requireTruckNumberForPosting.value
            ? el?.carrier_vehicle?.truck_number
            : true)
      )
      ?.map((e) => e.id) || []
);

const isAllAvailablePotentialPostingsSelected = computed(
  () =>
    allAvailablePotentialPostings.value?.length ===
      selectedPotentialCapacities.value?.length &&
    selectedPotentialCapacities.value?.length > 0
);

const showCapacityButtons = computed(() => {
  return (
    (selectedTab.value === 'Potential' && potential_trucks.value?.length > 0) ||
    selectedTab.value === 'Active' ||
    selectedTab.value === 'Expired'
  );
});

const handleSelectAllPotentialCapacities = (e) => {
  if (e) {
    selectedPotentialCapacities.value = allAvailablePotentialPostings.value;
  } else {
    selectedPotentialCapacities.value = [];
  }
};

const carrier_vehicles = computed(
  () => store.state.loads?.eld_carrier_vehicles
);

const filtered_carrier_vehicles_length = computed(() => {
  const postedTruckIds = new Set(
    truck_postings.value?.map((truck) => truck.carrier_vehicle?.id)
  );

  const filteredVehicles = carrier_vehicles.value?.filter(
    (vehicle) => !postedTruckIds.has(vehicle.id)
  );
  return filteredVehicles?.length || 0;
});

const truckPostingError = computed(
  () => store.state.loads?.truck_posting_error
);

const errorMessage = computed(() => {
  return Object.entries(truckPostingError?.value)?.map(([key, value]) => {
    if (!Array.isArray(value)) {
      return '';
    }

    let title;

    if (key === 'origin_area_id') {
      title = 'Origin';
    } else if (key === 'destination_area_id') {
      title = 'Destination';
    } else if (key === 'carrier_vehicle') {
      title = 'Vehicle';
    } else {
      title = capitalizeWords(key?.replace(/_/g, ' '));
    }

    const values = value?.reduce((acc, el, index) => {
      if (index > 0) {
        acc += ` and ${el}`;
      } else {
        acc += el;
      }

      return acc;
    }, '');

    return `${title}: ${values}`;
  });
});

const isCapacityModalVisible = ref(
  router.currentRoute.value.query?.modal === 'post-capacity' ? true : false
);

const toggleCapacityModal = () => {
  isCapacityModalVisible.value = !isCapacityModalVisible.value;
};

const handlePostTruckError = () => {
  isCapacityModalVisible.value = false;
  showTruckNotication.value = true;
};

const showTruckNotication = ref(false);

const handleCloseTruckNotification = () => {
  showTruckNotication.value = false;
};

const handleRetry = () => {
  handleCloseTruckNotification();
  toggleCapacityModal();
};

const failedCapacityNotifcation = () => {
  store.dispatch('toast/addToast', {
    component: CapacityNotification,
    props: {
      visible: true,
      title: 'Unable to Post Truck',
      toggleModal: toggleCapacityModal,
    },
    manuallyClose: true,
  });
};

const isBulkPostingPotentialTruckPostings = computed(
  () => store.state.loads.is_bulk_post_potential_truck_postings_loading
);

const handlePostSuggestedCapacities = async (ids) => {
  const truck_posting_ids = ids || selectedPotentialCapacities.value;

  const res = await store.dispatch('loads/bulkPostPotentialTruckPostings', {
    truck_posting_ids: truck_posting_ids,
  });

  if ([200, 201, 204].includes(res?.status)) {
    if (res?.data?.errors?.length > 0) {
      const errorText = res?.data?.errors?.map(
        (err) => `Truck ${err.id}: ${err.error}`
      );

      await store.dispatch('toast/addToast', {
        component: SuggestedCapacityNotification,
        props: {
          visible: true,
          title: 'Failed to post the following suggested capacities.',
          text: errorText,
        },
      });
    } else if (res?.data?.success?.length > 0) {
      const text = `You successfully posted ${res?.data?.success?.length} truck${res?.data?.success?.length > 1 ? 's' : ''}.`;

      await store.dispatch('toast/addToast', {
        component: SuggestedCapacityNotification,
        props: {
          visible: true,
          text: text,
        },
        duration: 10000,
      });
    }

    if (res?.data?.success?.length > 0) {
      handleTabSelection('Active');

      // refetch potential truck postings if successful response
      await store.dispatch('loads/getTruckPostings', {
        query: '?q[is_active_eq]=1',
      });
      await store.dispatch('loads/getPotentialTruckPostings', {});
    }
  } else {
    store.dispatch('toast/addToast', {
      component: SuggestedCapacityNotification,
      props: {
        visible: true,
        title: 'Failed to post suggested capacities.',
        text: 'Please try again.',
      },
      manuallyClose: true,
    });
  }
};
</script>
